import styled from 'styled-components';
import { color } from 'theme';

export const PageContainer = styled.div`
  position: relative;
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  opacity: ${(props) => (props.$isReady ? 1 : 0)};
  transition: opacity 0.5s ease-in;
  z-index: 1;
`;

export const BackgroundImage = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: -2;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url(${(props) => props.$imgUrl});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  animation: appearance 1.2s ease-in;

  @keyframes appearance {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

export const BlackOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: -1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  animation: appearance 1.2s ease-in;

  @keyframes appearance {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid ${(props) => props.$mainColor || color.red};
`;

export const PageTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${color.greyText};
  margin: 0;
`;

export const SiteName = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;

  .site-title {
    font-size: 1.8rem;
    font-weight: 600;
    color: ${(props) => props.$mainColor || color.red};
  }

  .site-url {
    font-size: 1.4rem;
    color: ${color.greyText};
  }
`;

export const SelectorContainer = styled.div`
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 2;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

export const StatCard = styled.div`
  background: ${(props) => props.$mainColorRgba(0.1)};
  border: 1px solid ${(props) => props.$mainColor || color.red};
  border-radius: 8px;
  padding: 1.5rem;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.$mainColorRgba(0.3)};
  }
`;

export const StatLabel = styled.div`
  font-size: 1.1rem;
  color: ${color.greyText};
  margin-bottom: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${color.white};
`;

export const ProductsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 2rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  overflow: hidden;
`;

export const TableHeader = styled.tr`
  background: ${(props) => props.$mainColor || color.red};
  color: ${color.white};
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background 0.2s ease;

  &:hover {
    background: ${(props) => props.$mainColorRgba(0.2)};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const TableCell = styled.td`
  padding: 1rem;
  font-size: 1.2rem;
  color: ${color.white};
  text-align: left;

  &:first-child {
    font-weight: 600;
  }
`;

export const LoadingMessage = styled.div`
  text-align: center;
  font-size: 1.4rem;
  color: ${color.greyText};
  padding: 3rem 0;
`;

export const ErrorMessage = styled.div`
  text-align: center;
  font-size: 1.3rem;
  color: ${color.red};
  padding: 1.5rem;
  background: rgba(255, 51, 102, 0.1);
  border: 1px solid ${color.red};
  border-radius: 8px;
  margin: 1.5rem 0;
`;

export const AccessDeniedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  text-align: center;

  h2 {
    font-size: 3.2rem;
    color: ${color.white};
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.6rem;
    color: ${color.greyText};
  }
`;

export const SectionHeader = styled.h2`
  color: ${(props) => props.$mainColor || color.red};
  margin-top: ${(props) => props.$marginTop || '2rem'};
  margin-bottom: 1rem;
  font-size: 1.8rem;
  font-weight: 600;
`;

export const SectionSubHeader = styled.h3`
  color: ${(props) => props.$mainColor || color.red};
  margin-top: ${(props) => props.$marginTop || '2rem'};
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 600;
`;

export const CollapsibleSection = styled.div`
  margin-top: 3rem;
  border: 1px solid ${(props) => props.$mainColor || color.red};
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
`;

export const CollapsibleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: ${(props) => props.$mainColorRgba(0.2)};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${(props) => props.$mainColorRgba(0.3)};
  }
`;

export const CollapsibleTitle = styled.h3`
  color: ${color.white};
  font-size: 1.6rem;
  font-weight: 600;
  margin: 0;
`;

export const CollapsibleIcon = styled.span`
  color: ${color.white};
  font-size: 1.8rem;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const CollapsibleContent = styled.div`
  max-height: ${(props) => (props.$isOpen ? '2000px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: ${(props) => (props.$isOpen ? '1.5rem' : '0 1.5rem')};
`;

export const SearchContainer = styled.div`
  margin-bottom: 1.5rem;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 1rem;
  font-size: 1.3rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${(props) => props.$mainColor || color.red};
  border-radius: 6px;
  color: ${color.white};
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: ${color.greyText};
  }

  &:focus {
    border-color: ${(props) => props.$mainColor || color.red};
    box-shadow: 0 0 0 2px ${(props) => props.$mainColorRgba(0.2)};
  }
`;

export const ClickableRow = styled(TableRow)`
  cursor: pointer;

  &:hover {
    background: ${(props) => props.$mainColorRgba(0.3)};
  }
`;

export const SmallText = styled.div`
  font-size: 1rem;
  color: ${color.greyText};
  margin-top: 0.4rem;
`;

export const StatLabelWithHelp = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
`;

export const HelpIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => props.$mainColorRgba(0.3)};
  color: ${color.white};
  font-size: 1rem;
  font-weight: bold;
  cursor: help;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => props.$mainColor};
    transform: scale(1.1);
  }

  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    padding: 0.8rem 1rem;
    background: rgba(0, 0, 0, 0.95);
    color: ${color.white};
    font-size: 1.2rem;
    font-weight: normal;
    border-radius: 6px;
    white-space: nowrap;
    z-index: 1000;
    border: 1px solid ${(props) => props.$mainColor};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }

  &:hover::before {
    content: '';
    position: absolute;
    bottom: calc(100% + 2px);
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${(props) => props.$mainColor};
    z-index: 1001;
  }
`;
